import React from "react";
//#region types
import { WintSuperkollAPIModelAnnualAccounts, WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { FileProfileInfo } from "models/propTypes/userProfilePropTypes";
import { FileEnums } from "models/enums";
import { DateHelper, UrlHelper } from "utils/helpers";
//#endregion
//#region components
import { Icon, RoundButton, Skeletons, TableAsync, ViewPanel } from "components/common";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnnualAccountService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";

const FinancialStatementsList: React.FC = (props) => {
  const annualAccountService = useAnnualAccountService();
  const [result, setResult] = React.useState<WintSuperkollAPIModelAnnualAccounts.FinancialStatement[]>([]);

  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);    
    annualAccountService.getFinancialStatements().then((res) =>{
      setResult(res ?? [])
      setLoading(false);      
    } );
  }, []);

  const onRowClick = (row: WintSuperkollAPIModelAnnualAccounts.FinancialStatement) => {
    if (row.reportType === WintSuperkollAPIModel.FinancialStatementType.Questionnaire && row.financialYearId) {
      navigate(UrlHelper.formatPath(`/Survey/${row.surveyId}`), {
        state: { background: location },
      });
    } else {
      navigate(UrlHelper.formatPath(`/FinancialStatement/${row.financialYearId}/${row.reportType}`), {
        state: { background: location },
      });
    }
  };

  const onDownloadClick = (row: WintSuperkollAPIModelAnnualAccounts.FinancialStatement, e: React.MouseEvent) => {
    e.stopPropagation();
    if (row.financialYearId) return annualAccountService.downloadPdf(row.financialYearId ?? "", row.reportType);
  };

  const fileNameFormatter = (
    row: WintSuperkollAPIModelAnnualAccounts.FinancialStatement,
    rowIndex: number,
    depth?: number
  ) => {
    return row.reportType === WintSuperkollAPIModel.FinancialStatementType.Questionnaire
      ? new FileProfileInfo(row.fileName ?? "", FileEnums.ContentTypes.Html.toString())
      : new FileProfileInfo(row.fileName ?? "", FileEnums.ContentTypes.ApplicationPDF.toString());
  };

  const downloadButtonFormatter = (row: WintSuperkollAPIModelAnnualAccounts.FinancialStatement) => {
    return row.reportType !== WintSuperkollAPIModel.FinancialStatementType.Questionnaire ? (
      <RoundButton className={cssExports.tableIconButton} onClick={onDownloadClick.bind(null, row)}>
        <Icon icon={IconDefinitionConstants.WDownload} />
      </RoundButton>
    ) : (
      <></>
    );
  };

  if( loading ) 
    return (
      <ViewPanel fitContent appearance="sm">
          <Skeletons.TableSkeleton />
      </ViewPanel>
    );

  return (
    <TableAsync<WintSuperkollAPIModelAnnualAccounts.FinancialStatement>
      properties={{
        fileName: {
          description: TranslationKeys.Common.Name,
          formatter: fileNameFormatter,
        },
        financialYear: {
          description: TranslationKeys.Common.YearPascal,
          formatter: (row: WintSuperkollAPIModelAnnualAccounts.FinancialStatement) =>
            row.financialYear ? DateHelper.formatFinancialYear(row.financialYear.start, row.financialYear.end) : "",
          width: 200,
          alignment: "center",
        },
        financialYearId: {
          description: TranslationKeys.Common.Download,
          formatter: downloadButtonFormatter,
          alignment: "center",
          width: 75,
        },
      }}
      filter={result}
      rowClickHandler={onRowClick}
    />
  );
};

export default FinancialStatementsList;
